/**
 * Message search (the Search tab, `/{ws}/search?q=`): Slack-style queries (logic/searchQuery — free text plus
 * `from:`, `in:`, `has:`, `before:` / `after:` / `on:`, `is:thread`), debounced, answered by the server. Each result
 * row carries what the list shows (conversation, author, time) and the message link that opens it in context.
 * Online only: offline, the last results stay with `offline` set.
 *
 * As you type, `suggest` offers filters and their values (logic/searchSuggest, Slack's search chips): the field
 * reports its caret (`setCaret`), arrows move the highlight, Enter / Tab take it (the edit comes back to the screen,
 * which writes it into the field and URL), Escape closes it until the next keystroke.
 */
import type { Message } from "@bundle/contracts";
import { hasCriteria, highlightTerms, parseSearchQuery } from "../logic/searchQuery.ts";
import { applySearchDate, applySearchSuggestion, searchSuggestContext, searchSuggestions, type SearchFilterKey, type SearchSuggestMenu, type SearchTextEdit } from "../logic/searchSuggest.ts";
import { messageUrl } from "../logic/shell.ts";
import { channelTitle } from "../logic/sidebar.ts";
import { memberName } from "../logic/workspace.ts";
import { isCollapsed } from "../logic/moderation.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type SearchResultRow = {
  id: string;
  message: Message;
  /** "#design", or the DM's people. */
  conversation: string;
  /** DM / group DM (no "#"). */
  isDm: boolean;
  authorId: string;
  authorName: string;
  authorAvatarUrl?: string;
  /** A thread reply. */
  inThread: boolean;
  /** Message link (logic/shell `messageUrl`): the conversation or thread, scrolled to the message. */
  url: string;
  /** From someone I blocked: shows as "Message from someone you blocked · Show" until `showBlocked` (logic/moderation). */
  collapsed?: true;
};

export type SearchViewState = {
  query: string;
  /** idle = nothing to search for; loading keeps the previous rows on screen. */
  status: "idle" | "loading" | "ready" | "error";
  results: SearchResultRow[];
  /** Filters the server couldn't resolve or understand (`from:@nobody`, `has:banana`). */
  unresolved: string[];
  /** Words of the free text, folded, for highlighting. */
  terms: string[];
  offline: boolean;
  /** The open suggestion menu and its highlighted row; absent when closed. */
  suggest?: SearchSuggestMenu & { active: number };
};

export type SearchSuggestKey = "up" | "down" | "enter" | "tab" | "escape";

/** Typing settles for this long before a search goes out. */
export const SEARCH_DEBOUNCE_MS = 250;

export class SearchViewModel extends ViewModel<SearchViewState> {
  private cancelTimer: (() => void) | undefined;
  /** Answers to older queries are dropped. */
  private ticket = 0;
  private lastMessages: Message[] = [];
  private caret = 0;
  /** Escape closed the menu: it stays closed until the text or caret changes. */
  private dismissed = false;
  /** Blocked people's results I chose to Show (this view only). */
  private shown: string[] = [];

  /** `tz`: my IANA time zone for the date filters (absent: the server uses my profile's). */
  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly tz?: string,
  ) {
    super({ query: "", status: "idle", results: [], unresolved: [], terms: [], offline: !ws.state.network });
    this.onDispose(() => this.cancelTimer?.());
    // Names and conversations come from the cache: re-derive rows when it changes.
    this.onDispose(ws.subscribe(() => this.rederive()));
  }

  /** The field changed (or the URL's `q` on open): searches after a short pause; `now` skips the pause. */
  setQuery(query: string, now = false): void {
    if (query === this.state.query && this.state.status !== "error") return;
    // Typing at the end keeps the caret at the end (the field reports other positions through setCaret).
    if (this.caret >= this.state.query.length || this.caret > query.length) this.caret = query.length;
    this.dismissed = false;
    this.refreshSuggest(query);
    this.cancelTimer?.();
    this.cancelTimer = undefined;
    const parsed = parseSearchQuery(query);
    if (!hasCriteria(parsed)) {
      this.ticket += 1;
      this.lastMessages = [];
      this.update({ query, status: "idle", results: [], unresolved: parsed.invalid, terms: [] });
      return;
    }
    this.update({ query, status: "loading", terms: highlightTerms(parsed.text) });
    if (now) this.run();
    else this.cancelTimer = this.ws.deps.clock.setTimer(SEARCH_DEBOUNCE_MS, () => this.run());
  }

  /** The field's caret moved (typing, clicks, arrows). */
  setCaret(caret: number): void {
    const c = Math.max(0, Math.min(caret, this.state.query.length));
    if (c === this.caret && (this.state.suggest !== undefined || this.dismissed)) return;
    this.caret = c;
    this.dismissed = false;
    this.refreshSuggest(this.state.query);
  }

  /** Closes the suggestions (Escape, a click elsewhere) until the next keystroke or caret move. */
  dismissSuggest(): void {
    this.dismissed = true;
    if (this.state.suggest !== undefined) this.update({ suggest: undefined });
  }

  /** Highlights row `index` (pointer hover). */
  highlightSuggest(index: number): void {
    const s = this.state.suggest;
    if (s === undefined || index < 0 || index >= s.items.length || index === s.active) return;
    this.update({ suggest: { ...s, active: index } });
  }

  /**
   * A field key while the menu is open: arrows move (wrapping), Enter / Tab take the highlighted row, Escape closes.
   * Returns whether the key was used, and the edit to apply when a row was taken.
   */
  suggestKey(key: SearchSuggestKey): { handled: boolean; edit?: SearchTextEdit } {
    const s = this.state.suggest;
    if (s === undefined) return { handled: false };
    if (key === "escape") {
      this.dismissSuggest();
      return { handled: true };
    }
    if (key === "up" || key === "down") {
      const n = s.items.length;
      this.update({ suggest: { ...s, active: (s.active + (key === "up" ? -1 : 1) + n) % n } });
      return { handled: true };
    }
    const edit = this.chooseSuggest(s.active);
    return edit === undefined ? { handled: false } : { handled: true, edit };
  }

  /** Takes row `index`: returns the new text and caret (the screen writes them back; setQuery follows). */
  chooseSuggest(index: number): SearchTextEdit | undefined {
    const s = this.state.suggest;
    const item = s?.items[index];
    if (s === undefined || item === undefined) return undefined;
    const edit = applySearchSuggestion(this.state.query, s, item);
    this.caret = edit.caret;
    this.dismissed = false;
    return edit;
  }

  /**
   * A date picked in the open menu's date picker (the menu is completing `before:` / `after:` / `on:` / `during:`):
   * returns the new text and caret, like chooseSuggest. `month` inserts the month (`during:2026-10`).
   */
  chooseDate(date: string, month = false): SearchTextEdit | undefined {
    const s = this.state.suggest;
    if (s?.key === undefined) return undefined;
    const edit = applySearchDate(this.state.query, s, s.key as SearchFilterKey, date, month);
    if (edit === undefined) return undefined;
    this.caret = edit.caret;
    this.dismissed = false;
    return edit;
  }

  private refreshSuggest(query: string): void {
    const cache = this.ws.state.cache;
    let suggest: SearchViewState["suggest"];
    if (!this.dismissed && cache !== undefined) {
      const now = this.ws.deps.clock.now();
      const menu = searchSuggestions(query, Math.min(this.caret, query.length), searchSuggestContext(cache, now, this.ws.deps.clock.tzOffsetMinutes(now)));
      if (menu !== undefined) {
        const prev = this.state.suggest;
        // Keep the highlighted row while it is still there.
        const kept = prev === undefined ? -1 : menu.items.findIndex((i) => prev.items[prev.active]?.insert === i.insert);
        suggest = { ...menu, active: Math.max(0, kept) };
      }
    }
    if (suggest !== undefined || this.state.suggest !== undefined) this.update({ suggest });
  }

  /** Try again after an error. */
  retry(): void {
    if (this.state.status === "error") this.run();
  }

  private run(): void {
    this.cancelTimer = undefined;
    const ticket = ++this.ticket;
    const q = this.state.query;
    if (this.state.status !== "loading") this.update({ status: "loading" });
    this.ws.deps.api.searchMessages(this.ws.wsId, q, this.tz).then(
      (r) => {
        if (this.isDisposed || ticket !== this.ticket) return;
        this.lastMessages = r.messages;
        this.update({ status: "ready", results: this.rows(r.messages), unresolved: r.unresolved ?? [], offline: false });
      },
      () => {
        if (this.isDisposed || ticket !== this.ticket) return;
        this.update({ status: "error", offline: !this.ws.state.network });
      },
    );
  }

  /** "Show" on a collapsed result from someone I blocked: reveals it in this view only. */
  showBlocked(messageId: string): void {
    if (this.shown.includes(messageId)) return;
    this.shown = [...this.shown, messageId];
    this.update({ results: this.rows(this.lastMessages) });
  }

  private rederive(): void {
    const offline = !this.ws.state.network;
    if (this.lastMessages.length === 0 && offline === this.state.offline) return;
    this.update({ results: this.rows(this.lastMessages), offline });
  }

  private rows(messages: Message[]): SearchResultRow[] {
    const cache = this.ws.state.cache;
    if (cache === undefined) return [];
    const out: SearchResultRow[] = [];
    for (const m of messages) {
      const c = cache.channels[m.channelId];
      const author = cache.members[m.authorId];
      const isDm = c !== undefined && (c.kind === "dm" || c.kind === "group_dm");
      const row: SearchResultRow = {
        id: m.id,
        message: m,
        conversation: c === undefined ? "" : isDm ? channelTitle(cache, c.id) : `#${c.name}`,
        isDm,
        authorId: m.authorId,
        authorName: memberName(cache, m.authorId),
        inThread: m.threadRootId !== undefined,
        url: messageUrl(cache.workspace.slug, m.channelId, m.id, m.threadRootId),
      };
      if (author?.avatarUrl !== undefined) row.authorAvatarUrl = author.avatarUrl;
      if (isCollapsed(m, cache.blockedUserIds, cache.meId, this.shown)) row.collapsed = true;
      out.push(row);
    }
    return out;
  }
}
